import React, { useState, useEffect } from 'react';
import { 
  EnvelopeIcon, 
  PaperAirplaneIcon, 
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import ComposeEmailModal from './ComposeEmailModal';

interface Email {
  id: number;
  organizationId: number;
  subject: string;
  body: string;
  htmlBody?: string;
  fromEmail: string;
  fromName: string;
  toEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
  status: 'draft' | 'sent' | 'failed';
  sentAt?: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  createdByFirstName?: string;
  createdByLastName?: string;
}

interface LeadEmailsTabProps {
  leadId: string;
  leadName: string;
  leadEmail: string;
}

const LeadEmailsTab: React.FC<LeadEmailsTabProps> = ({ leadId, leadName, leadEmail }) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEmails, setExpandedEmails] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showComposeEmailModal, setShowComposeEmailModal] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState<any>(null);

  useEffect(() => {
    fetchEmails();
    fetchSmtpSettings();
  }, [leadId]);

  const fetchSmtpSettings = async () => {
    try {
      const { emailApi } = await import('../services/emailApi');
      const settings = await emailApi.getSmtpSettings();
      setSmtpSettings(settings);
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
    }
  };

  const fetchEmails = async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      const response = await import('../services/api').then(module => 
        module.leadsAPI.getLeadEmails(leadId)
      );
      
      if (append) {
        setEmails(prev => [...prev, ...(response.data || [])]);
      } else {
        setEmails(response.data || []);
      }
      
      // For now, we'll show all emails since the API doesn't have pagination
      // In a real implementation, you'd check response.pagination
      setHasMore(false);
    } catch (err: any) {
      console.error('Error fetching emails:', err);
      setError('Failed to fetch emails');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      setCurrentPage(prev => prev + 1);
      fetchEmails(currentPage + 1, true);
    }
  };

  const handleEmailSent = async () => {
    // Refresh the emails list
    await fetchEmails();
    setShowComposeEmailModal(false);
  };

  const toggleExpanded = (emailId: number) => {
    setExpandedEmails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'draft':
        return <ClockIcon className="w-4 h-4" />;
      case 'failed':
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const truncateText = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <p>{error}</p>
        <button 
          onClick={() => fetchEmails()}
          className="mt-4 text-blue-600 hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
        <EnvelopeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Emails Found</h3>
        <p className="text-gray-600 mb-6">No emails have been sent to {leadName} ({leadEmail}) yet.</p>
        <button
          onClick={() => setShowComposeEmailModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Compose Email
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with Compose Email Button */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Emails</h3>
        <button
          onClick={() => setShowComposeEmailModal(true)}
          className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Compose Email
        </button>
      </div>

      {emails.map((email) => {
        const isExpanded = expandedEmails.has(email.id);
        return (
          <div key={email.id} className="bg-white rounded-lg shadow-sm border">
            {/* Compact Header */}
            <div 
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleExpanded(email.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <EnvelopeIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {email.subject}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(email.status)}`}>
                        {getStatusIcon(email.status)}
                        <span className="ml-1">{email.status.charAt(0).toUpperCase() + email.status.slice(1)}</span>
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span className="flex items-center">
                        <UserIcon className="w-3 h-3 mr-1" />
                        {email.fromName}
                      </span>
                      <span className="flex items-center">
                        <EnvelopeIcon className="w-3 h-3 mr-1" />
                        To: {email.toEmails.join(', ')}
                      </span>
                      {email.sentAt && (
                        <span className="flex items-center">
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {formatDate(email.sentAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-right text-xs text-gray-500">
                    <div>{formatDate(email.createdAt)}</div>
                    <div>by {email.createdByFirstName} {email.createdByLastName}</div>
                  </div>
                  {isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="space-y-4">
                  {/* Recipients */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Recipients</h5>
                    <div className="space-y-1 text-sm text-gray-700 bg-white p-3 rounded-lg">
                      <div>
                        <span className="font-medium">To:</span> {email.toEmails.join(', ')}
                      </div>
                      {email.ccEmails.length > 0 && (
                        <div>
                          <span className="font-medium">CC:</span> {email.ccEmails.join(', ')}
                        </div>
                      )}
                      {email.bccEmails.length > 0 && (
                        <div>
                          <span className="font-medium">BCC:</span> {email.bccEmails.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email Content */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Message</h5>
                    <div className="bg-white p-3 rounded-lg">
                      {email.htmlBody ? (
                        <div 
                          className="text-sm text-gray-700 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ 
                            __html: email.htmlBody.length > 500 
                              ? email.htmlBody.substring(0, 500) + '...' 
                              : email.htmlBody 
                          }}
                        />
                      ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {truncateText(email.body, 500)}
                        </p>
                      )}
                      {email.body.length > 500 && (
                        <button className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">
                          Show full message
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Status Information */}
                  {email.status === 'sent' && email.sentAt && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-green-900">
                          Email sent successfully on {formatDate(email.sentAt)}
                        </span>
                      </div>
                    </div>
                  )}

                  {email.status === 'failed' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <XCircleIcon className="w-4 h-4 text-red-600 mr-2" />
                        <span className="text-sm font-medium text-red-900">
                          Email failed to send
                        </span>
                      </div>
                    </div>
                  )}

                  {email.status === 'draft' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <ClockIcon className="w-4 h-4 text-yellow-600 mr-2" />
                        <span className="text-sm font-medium text-yellow-900">
                          Draft email - not sent yet
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Load More Button */}
      {hasMore && (
        <div className="text-center py-4">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loadingMore ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Loading...
              </>
            ) : (
              'Load More Emails'
            )}
          </button>
        </div>
      )}

      {/* Compose Email Modal */}
      {showComposeEmailModal && (
        <ComposeEmailModal
          onClose={() => setShowComposeEmailModal(false)}
          onEmailSent={handleEmailSent}
          smtpSettings={smtpSettings}
          preFilledToEmail={leadEmail}
        />
      )}
    </div>
  );
};

export default LeadEmailsTab;
